import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row, Col, Card, CardBody, Button, Spinner, Label, Input
} from 'reactstrap';
import { RootState, AppDispatch } from '../../store';
import { createCheckoutSession, loadStoredOnboardingData } from '../../slices/onboarding/thunk';
import { fetchPlans, selectPlanAction, updateSeatCount, updateBillingCycle, updateCurrency } from '../../slices/plans/thunk';
import { setOnboardingFlowId } from '../../slices/onboarding/reducer';
import CurrencySelector, { Currency } from '../../Components/CurrencySelector';
import LoadingState from '../../Components/LoadingState';
import { config } from '../../config/environment';
import { toast } from 'react-toastify';

const SelectPlans: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { loading, leadData, onboardingFlowId } = useSelector((state: RootState) => state.onboarding);
  const { plans, selectedPlan, selectedCurrency } = useSelector((state: RootState) => state.plans);

  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [checkingFlow, setCheckingFlow] = useState(true);
  const [paymentStep, setPaymentStep] = useState(false);

  useEffect(() => {
    dispatch(loadStoredOnboardingData());
  }, [dispatch]);

  useEffect(() => {
    const checkFlowStatus = async () => {
      const flowId = searchParams.get('flow') || localStorage.getItem('onboardingFlowId');

      if (!flowId) {
        toast.error('No onboarding session found');
        navigate('/getting-started');
        return;
      }

      try {
        const response = await fetch(config.api.onboardingFlowById(flowId), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const flow = await response.json();

          if (flow.status !== 'EMAIL_VERIFIED') {
            if (flow.status === 'INITIATED') {
              if (!flow.emailVerifiedVia) {
                navigate(`/verify-email?flow=${flowId}`);
              }
            } else if (flow.status === 'PAID') {
              navigate(`/create-organization?flow=${flowId}`);
            } else if (flow.status === 'COMPLETE') {
              navigate(`/complete?flow=${flowId}`);
            } else {
              navigate(`/getting-started?flow=${flowId}`);
            }
            return;
          }

          localStorage.setItem('onboardingFlowId', flowId);
          dispatch(setOnboardingFlowId(flowId));

          const storedLeadData = localStorage.getItem('leadData');
          if (!storedLeadData && flow.email) {
            const leadDataFromFlow = {
              email: flow.email,
              organizationName: flow.organizationName || '',
              firstName: '',
              lastName: '',
            };
            localStorage.setItem('leadData', JSON.stringify(leadDataFromFlow));
            dispatch(loadStoredOnboardingData());
          }
        } else {
          toast.error('Failed to load onboarding session');
          navigate('/getting-started');
        }
      } catch (error) {
        console.error('Error checking flow status:', error);
        toast.error('An error occurred. Please try again.');
        navigate('/getting-started');
      } finally {
        setCheckingFlow(false);
      }
    };

    checkFlowStatus();
  }, [searchParams, navigate, dispatch]);

  useEffect(() => {
    if (!checkingFlow) {
      dispatch(fetchPlans(selectedCurrency, billingCycle));
    }
  }, [dispatch, selectedCurrency, billingCycle, checkingFlow]);

  useEffect(() => {
    const preSelectedPlanId = searchParams.get('planId') || localStorage.getItem('preSelectedPlanId');
    if (preSelectedPlanId && plans.length > 0) {
      const preSelected = plans.find((p: any) => p.id === preSelectedPlanId);
      if (preSelected) {
        dispatch(selectPlanAction(preSelected.slug, preSelected.limits.minSeats, 'MONTHLY'));
        setPaymentStep(true);
      }
    }
  }, [dispatch, searchParams, plans]);

  const handlePlanSelection = (plan: any) => {
    dispatch(selectPlanAction(plan.slug, plan.limits.minSeats, billingCycle));
    setPaymentStep(true);
  };

  const handleBackToPlans = () => {
    setPaymentStep(false);
  };

  const handlePayment = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan first');
      return;
    }

    if (!leadData) {
      toast.error('Session data missing. Please go back to the beginning.');
      navigate('/getting-started');
      return;
    }

    const provider = selectedCurrency === 'USD' ? 'stripe' : 'razorpay';
    let currentFlowId = onboardingFlowId || localStorage.getItem('onboardingFlowId');

    if (!currentFlowId) {
      toast.error('No onboarding session found');
      navigate('/getting-started');
      return;
    }

    try {
      const result = await dispatch(createCheckoutSession({
        planSlug: selectedPlan.planSlug,
        seatCount: selectedPlan.seatCount,
        billingCycle: selectedPlan.billingCycle,
        onboardingFlowId: currentFlowId,
        email: leadData.email,
        provider,
        successUrl: `${window.location.origin}/payment-verification?flow=${currentFlowId}&provider=${provider}`,
        cancelUrl: `${window.location.origin}/select-plans?flow=${currentFlowId}`,
      }));

      if (provider === 'stripe') {
        if (result && result.sessionUrl) {
          window.location.href = result.sessionUrl;
        } else {
          toast.error('Failed to initialize Stripe checkout');
        }
      } else if (provider === 'razorpay') {
        if (result && result.subscriptionId) {
          await handleRazorpayPayment(result, currentFlowId);
        } else {
          toast.error('Failed to initialize Razorpay payment');
        }
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  const handleRazorpayPayment = async (razorpayData: any, flowId: string) => {
    try {
      if (typeof (window as any).Razorpay === 'undefined') {
        toast.error('Razorpay SDK not loaded. Please refresh and try again.');
        return;
      }

      const options: any = {
        key: razorpayData.keyId,
        name: 'Mentorgain',
        image: `${window.location.origin}/logo192.png`,
        prefill: {
          email: leadData?.email,
          name: `${leadData?.firstName} ${leadData?.lastName}`.trim(),
        },
        theme: { color: '#1226b4' },
      };

      if (razorpayData.isTrial && razorpayData.orderId) {
        options.order_id = razorpayData.orderId;
        options.amount = Math.round(razorpayData.amount * 100);
        options.currency = razorpayData.currency || 'INR';
        options.description = `Payment verification for ${selectedPlan?.selectedPlan?.name || 'Subscription'} (14-day trial)`;
      } else {
        options.subscription_id = razorpayData.subscriptionId;
        options.amount = Math.round(razorpayData.amount * 100);
        options.currency = razorpayData.currency || 'INR';
        options.description = `${selectedPlan?.selectedPlan?.name || 'Subscription'} - ${selectedPlan?.seatCount || 1} seats`;
      }

      options.handler = function (response: any) {
        toast.success('Payment successful! Verifying your payment...');
        setTimeout(() => {
          window.location.href = `${window.location.origin}/payment-verification?flow=${flowId}&provider=razorpay`;
        }, 1500);
      };

      options.modal = {
        ondismiss: function () {
          toast.info('Payment cancelled. You can try again anytime.');
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Error initializing Razorpay payment:', error);
      toast.error('Failed to initialize payment. Please try again.');
    }
  };

  const calculatePrice = () => {
    if (!selectedPlan) return 0;
    return selectedPlan.totalPrice || 0;
  };

  const handleBillingCycleChange = (cycle: 'MONTHLY' | 'YEARLY') => {
    setBillingCycle(cycle);
    if (selectedPlan) {
      dispatch(updateBillingCycle(cycle));
    }
  };

  const handleCurrencyChange = (currency: Currency) => {
    dispatch(updateCurrency(currency));
  };

  const handleSeatCountChange = (newSeatCount: number) => {
    if (selectedPlan) {
      dispatch(updateSeatCount(newSeatCount));
    }
  };

  if (checkingFlow) {
    return <LoadingState message="Loading plans..." />;
  }

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          {!paymentStep ? (
            <div className="onboarding-card-wrapper">
              <div className="text-center mb-4">
                <div className="mb-3">
                  <div className="onboarding-icon">
                    <i className="fa-solid fa-gem" />
                  </div>
                </div>
                <h4 className="mb-2 fw-bold">Choose Your Plan</h4>
                <p className="text-muted mb-3">Select the plan that best fits your organization</p>
                <div className="d-inline-flex align-items-center bg-info-subtle text-info px-3 py-2 rounded-pill">
                  <i className="fa-solid fa-gift me-2" />
                  <span className="fw-semibold" style={{ fontSize: '0.88rem' }}>Start with 14 days free trial</span>
                </div>
              </div>

              {/* Currency and Billing Cycle Selection */}
              <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3 mb-4">
                <CurrencySelector
                  selectedCurrency={selectedCurrency}
                  onCurrencyChange={handleCurrencyChange}
                />
                <div className="billing-toggle">
                  <button
                    type="button"
                    className={`billing-option ${billingCycle === 'MONTHLY' ? 'active' : ''}`}
                    onClick={() => handleBillingCycleChange('MONTHLY')}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    className={`billing-option ${billingCycle === 'YEARLY' ? 'active' : ''}`}
                    onClick={() => handleBillingCycleChange('YEARLY')}
                  >
                    Yearly <span className="badge bg-success ms-1" style={{ fontSize: '0.65rem' }}>-20%</span>
                  </button>
                </div>
              </div>

              {/* Plan Cards */}
              <Row className="g-4 mb-5 plan-cards-row">
                {plans.map((plan) => (
                  <Col key={plan.id} xs="12" lg="6">
                    <Card className={`h-100 plan-card ${plan.metadata?.isPopular ? 'plan-card--popular' : ''}`}>
                      {plan.metadata?.isPopular && (
                        <div className="position-absolute top-0 start-50 translate-middle">
                          <span className="plan-badge">Recommended</span>
                        </div>
                      )}
                      <CardBody className="d-flex flex-column p-4">
                        <div className="text-center mb-4">
                          <h5 className="fw-bold mb-2">{plan.name}</h5>
                          <p className="text-muted small mb-3">{plan.description}</p>
                          <div className="mb-2">
                            <span className="h2 fw-bold text-primary">
                              {selectedCurrency === 'USD' ? '$' : '₹'}{billingCycle === 'YEARLY' ? plan.pricing.yearlyPricePerSeat : plan.pricing.basePricePerSeat}
                            </span>
                            <span className="text-muted">/seat/{billingCycle.toLowerCase()}</span>
                          </div>
                          {billingCycle === 'YEARLY' && plan.pricing.yearlyDiscount && (
                            <div className="badge bg-success-subtle text-success me-2">
                              Save {Math.round(plan.pricing.yearlyDiscount * 100)}% annually
                            </div>
                          )}
                          <div className="badge bg-info-subtle text-info fw-semibold">
                            14-Day Free Trial
                          </div>
                          <div className="text-muted small mt-2">
                            Min {plan.limits.minSeats} seats
                          </div>
                        </div>

                        <div className="flex-grow-1 mb-4">
                          <div className="border-top pt-3">
                            <h6 className="mb-3 text-muted small fw-semibold text-uppercase">What's Included:</h6>
                            {plan.features && plan.features.length > 0 ? (
                              plan.features.map((feature, index) => (
                                <div key={feature.key || index} className="feature-item">
                                  <span className={`feature-icon ${feature.isHighlighted ? 'feature-icon--star' : 'feature-icon--check'}`}>
                                    <i className={`fa-solid ${feature.isHighlighted ? 'fa-star' : 'fa-circle-check'}`} />
                                  </span>
                                  <div className="flex-grow-1">
                                    <span className={`small ${feature.isHighlighted ? 'fw-semibold' : ''}`}>
                                      {feature.name}
                                    </span>
                                    {feature.description && (
                                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        {feature.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-muted small">No features listed</div>
                            )}
                          </div>
                        </div>

                        <Button
                          color={plan.metadata?.isPopular ? 'primary' : 'outline-primary'}
                          size="lg"
                          onClick={() => handlePlanSelection(plan)}
                          block
                        >
                          Select {plan.name}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ) : (
            // Payment View
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 col-xl-6">
                <div className="onboarding-card-wrapper">
                  <Card className="border-0">
                    <CardBody className="p-4 p-md-5">
                      <div className="text-center mb-4">
                        <h4 className="mb-2 fw-bold">Complete Your Purchase</h4>
                        <p className="text-muted">You're almost done! Let's process your payment.</p>
                      </div>

                      {selectedPlan && (
                        <div className="mb-4">
                          <Card className="bg-light border-0">
                            <CardBody>
                              <Row className="align-items-center">
                                <Col>
                                  <h6 className="mb-1">{selectedPlan.selectedPlan?.name || 'Selected Plan'}</h6>
                                  <small className="text-muted">{selectedPlan.selectedPlan?.description || ''}</small>
                                </Col>
                                <Col xs="auto">
                                  <div className="text-end">
                                    <div className="h5 mb-0 text-primary">
                                      {selectedCurrency === 'USD' ? '$' : '₹'}{calculatePrice()}
                                    </div>
                                    <small className="text-muted">{selectedPlan.seatCount} seats x {billingCycle.toLowerCase()}</small>
                                  </div>
                                </Col>
                              </Row>
                              <div className="border-top mt-3 pt-3">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="small text-muted">
                                    <i className="fa-solid fa-gift me-1" />
                                    Trial Period
                                  </span>
                                  <span className="badge bg-info-subtle text-info fw-semibold">
                                    14 Days Free
                                  </span>
                                </div>
                                <small className="text-muted d-block mt-2">
                                  Your subscription will begin after the 14-day trial period ends.
                                </small>
                              </div>
                            </CardBody>
                          </Card>

                          <div className="mt-4">
                            <Label for="seatCount" className="fw-semibold">Number of Seats</Label>
                            <div className="d-flex align-items-center gap-2">
                              <Button
                                color="outline-secondary"
                                size="sm"
                                onClick={() => handleSeatCountChange(Math.max(selectedPlan.selectedPlan?.limits.minSeats || 1, selectedPlan.seatCount - 1))}
                                disabled={selectedPlan.seatCount <= (selectedPlan.selectedPlan?.limits.minSeats || 1)}
                              >
                                -
                              </Button>
                              <Input
                                type="number"
                                value={selectedPlan.seatCount}
                                onChange={(e) => handleSeatCountChange(Math.max(selectedPlan.selectedPlan?.limits.minSeats || 1, parseInt(e.target.value) || selectedPlan.selectedPlan?.limits.minSeats || 1))}
                                className="text-center fw-bold"
                                style={{ width: '100px' }}
                                min={selectedPlan.selectedPlan?.limits.minSeats}
                                max={selectedPlan.selectedPlan?.limits.maxSeats ?? undefined}
                              />
                              <Button
                                color="outline-secondary"
                                size="sm"
                                onClick={() => handleSeatCountChange(selectedPlan.seatCount + 1)}
                                disabled={selectedPlan.selectedPlan?.limits.maxSeats ? selectedPlan.seatCount >= selectedPlan.selectedPlan.limits.maxSeats : false}
                              >
                                +
                              </Button>
                            </div>
                            <small className="text-muted mt-1 d-block">
                              Minimum: {selectedPlan.selectedPlan?.limits.minSeats || 1} seats
                              {selectedPlan.selectedPlan?.limits.maxSeats && `, Maximum: ${selectedPlan.selectedPlan.limits.maxSeats} seats`}
                            </small>
                          </div>
                        </div>
                      )}

                      <div className="mt-4 pt-3 border-top">
                        <div className="text-center mb-3">
                          <p className="text-muted small mb-0">
                            <i className="fa-solid fa-lock me-1" />
                            {selectedCurrency === 'USD'
                              ? 'Secure payment via Stripe'
                              : 'Secure payment via Razorpay'
                            }
                          </p>
                        </div>

                        <Button
                          color="primary"
                          size="lg"
                          block
                          onClick={handlePayment}
                          disabled={loading}
                          className="mb-3"
                        >
                          {loading ? <Spinner size="sm" className="me-2" /> : null}
                          Pay with {selectedCurrency === 'USD' ? 'Stripe' : 'Razorpay'}
                        </Button>

                        <Button
                          color="link"
                          block
                          onClick={handleBackToPlans}
                          className="text-decoration-none"
                        >
                          <i className="fa-solid fa-arrow-left me-1" />
                          Back to Plans
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectPlans;
